"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowUpRight,
  Bot,
  Database,
  Workflow,
} from "lucide-react";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

const capabilities: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
  href: string;
  linkLabel: string;
}> = [
  {
    title: "Autonomous AI Agents",
    description:
      "Multi-step agents that plan, decide, and execute your workflows end to end — not chatbots that stop at answering.",
    icon: Bot,
    href: "/services/ai-agents/",
    linkLabel: "AI agent development",
  },
  {
    title: "RAG Knowledge Systems",
    description:
      "Your documents become answers. Retrieval-augmented assistants fluent in English, Bahasa Malaysia and Chinese.",
    icon: Database,
    href: "/services/ai-solutions/",
    linkLabel: "AI agency in Malaysia",
  },
  {
    title: "Custom AI Assistants",
    description:
      "Sales and support assistants built for your business and trained on your own content.",
    icon: Workflow,
    href: "/services/conversational-ai/",
    linkLabel: "Explore conversational AI",
  },
];

interface AgenticShowcaseSectionProps {
  locale?: Locale;
}

export default function AgenticShowcaseSection({
  locale = defaultLocale,
}: AgenticShowcaseSectionProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 max-w-3xl">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "What agentic means")}
          </p>
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {t(locale, "AI that doesn't just answer.")}{" "}
            <span className="whitespace-nowrap">
              {t(locale, "It")}{" "}
              <span className="emphasis-underline">{t(locale, "acts.")}</span>
            </span>
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(
              locale,
              "Four building blocks we combine to turn AI strategy into deployed, working systems for your business.",
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {capabilities.map(
            ({ title, description, icon: Icon, href, linkLabel }, index) => (
              <motion.div
                key={title}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                  ease: [0.21, 0.6, 0.35, 1],
                }}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="mb-6 flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <span className="font-mono text-sm text-neutral-muted/60">
                    0{index + 1}
                  </span>
                </div>

                <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="mb-6 flex-1 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, description)}
                </p>

                <Link
                  href={getLocalizedPathname(href, locale)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-200 group-hover:gap-2.5"
                >
                  {t(locale, linkLabel)}
                  <ArrowUpRight className="h-4 w-4" aria-hidden />
                </Link>
              </motion.div>
            ),
          )}
        </div>
      </div>
    </section>
  );
}
